import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "./style.scss";

import PopUpOutline from "../PopUpOutline";
import constants from "../../shared/constants";
import recordingConstants from "../../shared/constants/recordingConstants";
import {
  deleteRecording,
  getCPVRRecordingStatus,
  doesCPVREventRecordingExist,
  doesCPVRSeriesRecordingExist,
  navigateToPVRManager,
  isCPVRRecordingInProgress,
  getRecordingSystemType,
  getTeamRecordingParams,
  getExistingTeamEvent,
  isNotLiveTeamEvent,
} from "../../shared/utils/recordingHelper";
import { useReducers } from "../../shared/hooks/useReducer";
import { deleteRecordingAction } from "../../pages/RecordingsPage/state/actions";
import { ANALYTICS_STORAGE_KEYS, LINK_INFO } from "../../shared/constants/analytics";
import { setSessionStorage } from "../../shared/utils/sessionStorage";
import { getAutoGeneratedObject, getAutogeneratedEpisodeString } from "../../shared/utils";
import { showModalPopup } from "../../App/state/actions";
const { RECORDING_PARAMS, RECORDING_PACKAGES } = recordingConstants;
const { REDUCER_TYPE, MODAL_TYPES } = constants;

const RecordingModal = ({
  closeModal,
  scheduleRecordingHandler,
  recordingInfo,
  editRecordingHandler,
  openCancelRecordingModal = () => {},
  scheduleTeamRecording,
  isSportsEventPage,
}) => {
  const { t: translate } = useTranslation();
  const { provider: appProvider, userProfile, featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isTeamRecordingsEnabled } = featureToggles;
  const isMR = getRecordingSystemType(userProfile) === RECORDING_PACKAGES.PACKAGE_NAME.LPVRMediaroom_TP;
  const dispatch = useDispatch();

  let recordingEventScheduledCheck, recordingSeriesScheduledCheck, recordingEventConflictCheck;
  let cpvrEventRecordingStatus, cpvrSeriesRecordingStatus, seriesId;
  let isSeriesConflict = false,
    isProgramConflict = false,
    isMREditable = false,
    isRecordingNow = false,
    hasRecording = false,
    hasSeriesRecording = false,
    isConflict = false;

  const seriesTitle = useMemo(() => {
    if (isSportsEventPage) {
      const sportsName = recordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports?.name;
      if (sportsName) {
        return sportsName;
      }
    }
    return recordingInfo?.assetToRecord?.metadata?.title;
  }, [isSportsEventPage, recordingInfo]);

  const existingTeamInfo = getExistingTeamEvent(recordingInfo, isTeamRecordingsEnabled);

  let episodeInfo = null;
  if (recordingInfo) {
    const autoGeneratedObject = getAutoGeneratedObject(recordingInfo.assetToRecord.metadata);

    if (isMR) {
      recordingEventScheduledCheck = recordingInfo.recordingEventScheduledCheck;
      recordingSeriesScheduledCheck = recordingInfo.recordingSeriesScheduledCheck;
      recordingEventConflictCheck = recordingInfo.recordingEventConflictCheck;

      // Currently recordings in a "conflict" status is specific to MediaRoom. CPVR simply does not create a recording if max concurrent recordings limit is reached
      isSeriesConflict = recordingInfo.recordingSeriesConflictCheck;
      isProgramConflict = recordingEventConflictCheck ? true : false;
      isConflict = isSeriesConflict || isProgramConflict;
      isMREditable = !isConflict && (recordingEventScheduledCheck || recordingSeriesScheduledCheck);
      isRecordingNow = false;
      seriesId = recordingInfo.mediaRoom?.seriesId;
    } else {
      cpvrEventRecordingStatus = getCPVRRecordingStatus(recordingInfo);
      cpvrSeriesRecordingStatus = getCPVRRecordingStatus(recordingInfo, true);
      isRecordingNow = isCPVRRecordingInProgress(cpvrEventRecordingStatus);
      if (recordingInfo.assetToRecord?.metadata) {
        seriesId = recordingInfo.assetToRecord.metadata.seriesId;
      }
      hasRecording =
        recordingInfo?.eventRecordingItem &&
        (doesCPVREventRecordingExist(cpvrEventRecordingStatus) ||
          doesCPVRSeriesRecordingExist(cpvrSeriesRecordingStatus));
      hasSeriesRecording = doesCPVRSeriesRecordingExist(cpvrSeriesRecordingStatus);
    }
    episodeInfo = getAutogeneratedEpisodeString(
      autoGeneratedObject,
      isMR ? recordingInfo : recordingInfo.assetToRecord?.metadata,
      false
    );
  }
  let modalHeaderText, firstCTAText;

  const episodeInfoExists = episodeInfo?.trim().length !== 0;
  if (isSportsEventPage && existingTeamInfo?.length > 0) {
    const action = RECORDING_PARAMS[isRecordingNow ? "CANCEL" : "EDIT"];
    // Prefer team event edit CTA over normal sport event
    modalHeaderText = translate("edit_recording");
    firstCTAText = translate(`${action}_event`);
  } else if (isMREditable || hasRecording) {
    modalHeaderText = translate("edit_recording");
    // As per TCDWC-960, in progress CPVR recordings will not be editable, so instead we allow the user to cancel the recording
    const action = RECORDING_PARAMS[isRecordingNow ? "CANCEL" : "EDIT"];
    firstCTAText = episodeInfoExists
      ? `${translate(action)} ${episodeInfo}`
      : isSportsEventPage
      ? translate(`${action}_event`)
      : translate(`${action}_program`);
  } else if (isConflict) {
    modalHeaderText = translate("recordings_added_waitlist");
    firstCTAText = translate(RECORDING_PARAMS.MANAGE_RECORDINGS);
  } else {
    modalHeaderText =
      isSportsEventPage && existingTeamInfo?.length ? translate("edit_recording") : translate("recording_configure");
    firstCTAText = episodeInfoExists
      ? `${translate(RECORDING_PARAMS.RECORD)} ${episodeInfo}`
      : isSportsEventPage
      ? translate(`${RECORDING_PARAMS.RECORD}_event`)
      : translate(`${RECORDING_PARAMS.RECORD}_program`);
  }

  const manageRecordingHandler = () => {
    navigateToPVRManager(true); // Redirect user to PVR manager scheduled page to manage the conflict
    setSessionStorage(ANALYTICS_STORAGE_KEYS.LINK, `${LINK_INFO.MANAGE_RECORDINGS};${LINK_INFO.RECORDING_PROMPT}`);
    closeModal();
  };

  const conflictRecordingTitle = () => {
    const conflictTextHead = translate("recordings_many");
    const conflictText = translate("recordings_reschedule");
    return (
      <>
        <p>{conflictTextHead}</p>
        <p>{conflictText}</p>
      </>
    );
  };

  const eventRecordingHandler = (typeOfRecording, recordingParams, teamUmId) => {
    if (
      (typeOfRecording !== RECORDING_PARAMS.TEAM &&
        (isMREditable || (typeOfRecording === RECORDING_PARAMS.EVENT && hasRecording))) ||
      (typeOfRecording === RECORDING_PARAMS.SERIES && hasSeriesRecording) ||
      (typeOfRecording === RECORDING_PARAMS.EVENT && existingTeamInfo?.length > 0)
    ) {
      // As per TCDWC-960, in progress CPVR recordings will not be editable, so instead we allow the user to cancel the recording
      if (isRecordingNow && typeOfRecording === RECORDING_PARAMS.EVENT) {
        openCancelRecordingModal(isSportsEventPage ? existingTeamInfo : null);
      } else if (isSportsEventPage && typeOfRecording === RECORDING_PARAMS.EVENT && existingTeamInfo?.length > 0) {
        editRecordingHandler(recordingInfo, RECORDING_PARAMS.TEAM_EVENT);
        closeModal();
      } else {
        if (typeOfRecording === RECORDING_PARAMS.EVENT && (isMREditable || hasRecording)) {
          editRecordingHandler(recordingInfo, typeOfRecording);
        } else if (
          typeOfRecording === RECORDING_PARAMS.SERIES &&
          (recordingSeriesScheduledCheck || hasSeriesRecording)
        ) {
          editRecordingHandler(recordingInfo, typeOfRecording);
        } else {
          scheduleRecordingHandler(typeOfRecording, recordingInfo);
        }
      }
      closeModal();
    } else if (isConflict) {
      manageRecordingHandler();
    } else if (typeOfRecording === RECORDING_PARAMS.TEAM) {
      const isRecordingExists = recordingParams?.recordingExists;
      const isNotLiveTeamEventRecording = isTeamRecordingsEnabled
        ? isNotLiveTeamEvent(recordingInfo?.assetToRecord)
        : false;

      const setTeamRecording = () => {
        scheduleTeamRecording({
          filter: RECORDING_PARAMS.SPORTS_TEAM,
          filterBy: { teamUmId },
        });
        closeModal();
      };

      if (isRecordingExists) {
        editRecordingHandler(
          { ...recordingParams, assetToRecord: recordingInfo?.assetToRecord },
          RECORDING_PARAMS.TEAM
        );
        closeModal();
      } else if (isNotLiveTeamEventRecording) {
        const metadata = recordingInfo?.assetToRecord?.metadata;
        const autoGeneratedObject = getAutoGeneratedObject(metadata);

        closeModal();
        dispatch(
          showModalPopup(MODAL_TYPES.ERROR, {
            title: translate("team_recording_first_run"),
            message: translate("team_recording_rerun").replace(
              "%s",
              getAutogeneratedEpisodeString(autoGeneratedObject, metadata)
            ),
            isCloseable: true,
            closeErrorModal: true,
            className: "team-rerun-modal",
            endButtonClickHandlerOverride: setTeamRecording,
          })
        );
      } else {
        setTeamRecording();
      }
    } else {
      scheduleRecordingHandler(typeOfRecording, recordingInfo);
      closeModal();
    }
  };

  const closeRecordingModal = () => {
    if (isConflict) {
      const recordingInfoDetails = recordingInfo.recordingSeriesCheck || recordingEventConflictCheck;
      recordingInfoDetails.recordingInfo = recordingInfo;
      deleteRecording(appProvider, true, recordingInfoDetails, null, dispatch(deleteRecordingAction), true);
    }
    closeModal();
  };

  // Missing POE translation for some keys hence using this method to replace the strings with the desired ones.
  const recordingCTALabelFormatter = (message) => {
    const ctaText = translate(RECORDING_PARAMS.SERIES_TEXT);
    if (message?.includes(ctaText.toLowerCase())) {
      message = message.replace(ctaText.toLowerCase(), ctaText);
    }
    return message;
  };

  const addTeamRecordingOptions = () => {
    let sports = recordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports;
    if (!isMR && isTeamRecordingsEnabled && sports?.homeUmId && sports?.guestUmId) {
      let teams = [
        { umId: sports?.homeUmId, name: sports?.homeAbbreviation || sports?.homeTeam },
        { umId: sports?.guestUmId, name: sports?.guestAbbreviation || sports?.guestTeam },
      ];

      return teams.map((team) => {
        // Check if recording exists for this team
        const recordingParams = getTeamRecordingParams(
          team.umId,
          recordingInfo?.seriesRecordingsList,
          recordingInfo?.assetToRecord?.id || recordingInfo?.assetToRecord?.metadata?.contentId
        );
        const ctaTextKey = recordingParams?.recordingExists
          ? translate("edit_team_title").replace("%s", team.name)
          : translate("record_team_title").replace("%s", team.name);

        // Return array of CTAs for each team
        return (
          <button
            key={team.umId}
            className="recording-item-title shaded"
            onClick={() => eventRecordingHandler(RECORDING_PARAMS.TEAM, recordingParams, team.umId)}
          >
            {ctaTextKey}
          </button>
        );
      });
    }
    return null;
  };

  const getCTAElements = () => {
    const firstCTA = (
      <button
        className="recording-item-title shaded"
        title={firstCTAText}
        onClick={() => eventRecordingHandler(RECORDING_PARAMS.EVENT)}
      >
        {firstCTAText}
      </button>
    );

    let secondCTA;
    if ((seriesId && !isConflict) || isConflict) {
      let secondCTAMsgKey;
      if (isConflict) {
        secondCTAMsgKey = "recordings_cancel";
      } else if (seriesId && (recordingSeriesScheduledCheck || hasSeriesRecording)) {
        secondCTAMsgKey = isSportsEventPage
          ? translate("edit_series_title").replace("%s", seriesTitle)
          : RECORDING_PARAMS.EDIT_SERIES;
      } else {
        secondCTAMsgKey = isSportsEventPage
          ? translate("record_series_title").replace("%s", seriesTitle)
          : RECORDING_PARAMS.RECORD_SERIES;
      }

      secondCTA = (
        <button
          className="recording-item-title shaded"
          onClick={() => (isConflict ? closeRecordingModal() : eventRecordingHandler(RECORDING_PARAMS.SERIES))}
        >
          {recordingCTALabelFormatter(translate(secondCTAMsgKey))}
        </button>
      );
    }
    let teamRecordingsCTA = isSportsEventPage ? addTeamRecordingOptions() : null;

    const closeCTA = (
      <button className="recording-item-title" onClick={() => closeModal()}>
        {translate(isConflict ? "close" : RECORDING_PARAMS.CANCEL)}
      </button>
    );

    return (
      <>
        {firstCTA}
        {secondCTA}
        {teamRecordingsCTA}
        {closeCTA}
      </>
    );
  };

  return modalHeaderText && firstCTAText ? (
    <>
      <div className="recording-modal-backdrop" />
      <div className="recording-modal-container">
        <div className="recording-modal-popup-container">
          <PopUpOutline className="recording-modal-popup">
            <div className="recording-type">{modalHeaderText}</div>
            <div className="recording-item">
              <div className="recording-item-details">
                {isConflict ? <div className="recording-subtitle">{conflictRecordingTitle()}</div> : null}
                {getCTAElements()}
              </div>
            </div>
          </PopUpOutline>
        </div>
      </div>
    </>
  ) : null;
};

export default RecordingModal;
